import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronsUpDown, LogOut, Monitor, Moon, Sun, UserRound } from 'lucide-react';
import { api } from '@/lib/api';
import { landingPath, useMe } from '@/lib/auth';
import { NAV, BUILT } from '@/lib/nav';
import { useTheme, type ThemePref } from '@/lib/theme';
import { ROLE_LABEL, type Program } from '@/lib/types';
import { cn, initials } from '@/lib/utils';
import { BlackWolfLogo, ClientLogo, HelmWordmark } from '@/components/brand';
import { Badge, Kbd, Spinner } from '@/components/ui/misc';
import { TaskPanelHost } from '@/components/tracker/TaskPanel';
import { AskPanel } from '@/components/ai/Assistant';
import { GuideButton } from '@/guide/Guide';
import { RaidPanelHost } from '@/components/raid/RaidPanel';
import { QuickAddProvider } from '@/components/tracker/QuickAdd';
import { CommandPalette } from '@/components/tracker/CommandPalette';
import { HealthDot } from '@/components/tracker/bits';
import { HEALTH_LABEL, useTracker } from '@/lib/tracker';

export function useProgram(programId: string | undefined) {
  return useQuery({
    queryKey: ['program', programId],
    queryFn: () => api.get<Program>(`/api/programs/${programId}`),
    enabled: Boolean(programId),
  });
}

export function AppShell() {
  const { programId } = useParams();
  const { data: me } = useMe();
  const program = useProgram(programId);
  const location = useLocation();

  if (program.isLoading || !me) return <Spinner className="h-full" />;
  if (program.error || !program.data) {
    const first = me.programs[0];
    return first && first.id !== programId ? <Navigate to={landingPath(first)} replace /> : <NoProgram />;
  }
  const p = program.data;
  const caps = new Set(p.capabilities);
  const items = NAV.filter((n) => !n.requires || caps.has(n.requires));

  return (
    <QuickAddProvider program={p}>
      <div className="flex h-full">
        <aside className="flex w-60 shrink-0 flex-col border-r border-border bg-surface">
          <div className="flex h-14 items-center border-b border-border px-4">
            <Link
              to={`/p/${p.id}/${p.membership.role === 'EXECUTIVE' ? 'executive' : p.membership.role === 'CONTRIBUTOR' ? 'my-work' : 'tracker'}`}
            >
              <HelmWordmark />
            </Link>
          </div>
          <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Main">
            {items.map((n) => (
              <NavLink
                key={n.path}
                to={`/p/${p.id}/${n.path}`}
                className={({ isActive }) =>
                  cn(
                    'group mb-0.5 flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13.5px] transition-colors',
                    isActive
                      ? 'bg-brand-soft font-medium text-brand-text'
                      : 'text-muted hover:bg-surface-2 hover:text-text',
                  )
                }
              >
                <n.icon className="size-4 shrink-0" />
                <span className="flex-1 truncate">{n.label}</span>
                {!BUILT.has(n.path) && (
                  <span className="text-[10px] tabular text-subtle opacity-0 transition-opacity group-hover:opacity-100">
                    S{n.sprint}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-border px-4 py-3">
            <BlackWolfLogo className="origin-left scale-[0.8]" />
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center gap-4 border-b border-border bg-surface px-5">
            {p.logo && (
              <ClientLogo programId={p.id} version={p.logo.version} clientName={p.clientName} imgClassName="h-8" />
            )}
            <div className="min-w-0">
              <ProgramSwitcher current={p} />
              <p className="truncate text-xs text-muted">
                {p.clientName} · {p.consultantName}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-3">
              <ProgramHealth programId={p.id} />
              <GuideButton program={p} />
              <AskPanel program={p} />
              <span className="hidden text-xs text-subtle lg:inline">
                <Kbd>Ctrl K</Kbd> to jump
              </span>
              <Badge tone={p.membership.role === 'ADMIN' ? 'brand' : 'neutral'}>{ROLE_LABEL[p.membership.role]}</Badge>
              <UserMenu name={me.user.name} email={me.user.email} />
            </div>
          </header>
          <main className="min-h-0 flex-1 overflow-y-auto">
            {p.purgedAt && !location.pathname.includes('/admin') ? (
              <Navigate to={`/p/${p.id}/admin/data`} replace />
            ) : (
              <Outlet context={p} />
            )}
          </main>
          <TaskPanelHost program={p} />
          <RaidPanelHost program={p} />
          <CommandPalette program={p} />
        </div>
      </div>
    </QuickAddProvider>
  );
}

/** Program health in the header: the worst health among phases in flight. */
function ProgramHealth({ programId }: { programId: string }) {
  const { data } = useTracker(programId);
  if (!data) return null;
  return (
    <span
      className="hidden items-center gap-1.5 text-xs text-muted md:flex"
      title="Worst health among phases in flight"
    >
      <HealthDot level={data.health} /> {HEALTH_LABEL[data.health]}
    </span>
  );
}

function ProgramSwitcher({ current }: { current: Program }) {
  const { data: me } = useMe();
  const nav = useNavigate();
  const others = me?.programs.filter((x) => x.id !== current.id) ?? [];
  if (!others.length) return <p className="truncate text-sm font-semibold">{current.name}</p>;
  return (
    <label className="relative flex items-center gap-1 text-sm font-semibold">
      <select
        className="appearance-none bg-transparent pr-5 font-semibold focus:outline-none"
        value={current.id}
        onChange={(e) => {
          const target = me!.programs.find((x) => x.id === e.target.value)!;
          nav(landingPath(target));
        }}
      >
        {me!.programs.map((x) => (
          <option key={x.id} value={x.id}>
            {x.clientShortName}: {x.name}
          </option>
        ))}
      </select>
      <ChevronsUpDown className="pointer-events-none absolute right-0 size-3.5 text-muted" />
    </label>
  );
}

const THEMES: Array<{ v: ThemePref; label: string; icon: typeof Sun }> = [
  { v: 'light', label: 'Light', icon: Sun },
  { v: 'dark', label: 'Dark', icon: Moon },
  { v: 'system', label: 'System', icon: Monitor },
];

function UserMenu({ name, email }: { name: string; email: string }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { programId } = useParams();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  async function signOut() {
    await api.post('/api/auth/logout').catch(() => {});
    qc.clear();
    nav('/signin', { replace: true });
  }

  return (
    <div ref={ref} className="relative">
      <button
        className="flex size-8 items-center justify-center rounded-full bg-surface-3 text-xs font-semibold text-text hover:ring-2 hover:ring-border-strong"
        aria-label="Account menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {initials(name)}
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-40 w-64 rounded-lg border border-border bg-surface p-1.5 shadow-lg">
          <div className="px-2.5 py-2">
            <p className="truncate text-sm font-medium">{name}</p>
            <p className="truncate text-xs text-muted">{email}</p>
          </div>
          <div className="my-1 h-px bg-border" />
          <Link
            to={`/p/${programId}/account`}
            onClick={() => setOpen(false)}
            className="flex h-8 items-center gap-2 rounded-md px-2.5 text-sm hover:bg-surface-2"
          >
            <UserRound className="size-4 text-muted" /> Account and security
          </Link>
          <div className="flex items-center gap-1 px-2.5 py-1.5">
            {THEMES.map((t) => (
              <button
                key={t.v}
                onClick={() => setTheme(t.v)}
                aria-pressed={theme === t.v}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1 rounded-md py-1 text-xs',
                  theme === t.v ? 'bg-surface-3 font-medium text-text' : 'text-muted hover:bg-surface-2',
                )}
              >
                <t.icon className="size-3.5" />
                {t.label}
              </button>
            ))}
          </div>
          <div className="my-1 h-px bg-border" />
          <button
            onClick={signOut}
            className="flex h-8 w-full items-center gap-2 rounded-md px-2.5 text-sm hover:bg-surface-2"
          >
            <LogOut className="size-4 text-muted" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function NoProgram() {
  return (
    <div className="flex h-full items-center justify-center p-8 text-center">
      <div>
        <p className="font-medium">You are not a member of any program yet.</p>
        <p className="mt-1 text-sm text-muted">Ask your program admin for an invitation.</p>
      </div>
    </div>
  );
}
